<template>
    <CustomLink
        :to="item.to"
        :href="item.href ? item.href : null"
        cursor-type="view"
        :cursor-text="item.cursorText"
        :class="[
            'ImageCard',
            {
                'ImageCard--ignore-theme-colour': theme?.ignoreThemeColour,
                'ImageCard--small-text': props.smallText,
            },
        ]"
        :style="{
            '--image-card-base-colour': theme?.ignoreThemeColour
                ? null
                : theme?.data?.theme_base_colour,
        }"
    >
        <div class="ImageCard__image-container">
            <SmartImage
                class="ImageCard__image"
                :url="item.image.path"
                :placeholder-image="theme?.data?.theme_logo"
                :srcs="
                    item.image.dimensions?.width
                        ? [item.image.dimensions]
                        : [{ width: 588, height: 331 }]
                "
                :width="
                    item.image.dimensions?.width
                        ? item.image.dimensions.width
                        : 588
                "
                :height="
                    item.image.dimensions?.height
                        ? item.image.dimensions?.height
                        : 331
                "
            />
            <div
                class="ImageCard__chip"
                v-if="
                    (theme?.data?.theme_logo || theme?.data?.theme_icon) &&
                    !theme.hideThemeLogo
                "
            >
                <CustomChip
                    :bg-colour="theme?.data?.theme_chip_colour"
                    :icon="theme?.data?.theme_icon"
                    :icon-colour="theme?.data?.theme_chip_icon_colour"
                    :image="theme?.data?.theme_logo"
                />
            </div>
        </div>
        <SmartImage
            v-if="item.logo?.path"
            class="ImageCard__logo"
            :url="item.logo.path"
            :srcs="
                item.logo.dimensions.width
                    ? [item.logo.dimensions]
                    : [{ width: 588, height: 331 }]
            "
            :width="
                item.logo.dimensions.width ? item.logo.dimensions.width : 140
            "
            :height="
                item.logo.dimensions.height ? item.logo.dimensions.height : 60
            "
            :with-placeholder="false"
        />
        <ul v-if="item.listInfo?.length" class="ImageCard__list-info">
            <li v-for="info in item.listInfo">
                {{ info.text }}
                <span class="ImageCard__list-info-separator"></span>
            </li>
        </ul>
        <h3 v-if="item.title" class="ImageCard__title">
            <span class="ImageCard__title-text">{{ item.title }}</span>
        </h3>
        <ul class="ImageCard__tags" v-if="item.tags.filter((a) => a)?.length">
            <li v-for="tag in item.tags.filter((a) => a)">
                <CustomTag
                    :text="tag"
                    class="ImageCard__tag--dark-theme"
                    :theme="
                        theme?.ignoreThemeColour
                            ? ''
                            : theme?.data?.theme_base_colour
                    "
                />
                <CustomTag
                    :text="tag"
                    :filled="true"
                    :theme="
                        theme?.ignoreThemeColour
                            ? ''
                            : theme?.data?.theme_base_colour
                    "
                    class="ImageCard__tag--light-theme"
                />
            </li>
        </ul>
    </CustomLink>
</template>

<script setup lang="ts">
interface Image {
    path: string
    dimensions: {
        width: number
        height: number
    }
}

interface ListInfo {
    text: string
}

interface ImageCardItem {
    image: Image
    title: string
    tags: Array<string>
    listInfo: Array<ListInfo>
    logo: Image
    to: string
    href: string
    cursorText: string
}

interface ImageCardProps {
    item: ImageCardItem
    theme?: {
        ignoreThemeColour?: boolean
        hideThemeLogo?: boolean
        data?: {
            theme_logo: {
                url: string
            }
            theme_chip_icon_colour: string
            theme_chip_colour: string
            theme_icon: string
            theme_base_colour: string
        }
    }
    smallText?: false
}

const props = withDefaults(defineProps<ImageCardProps>(), {
    theme: () => ({}),
})
</script>

<style lang="scss">
.theme--white {
    .ImageCard {
        color: var(--black);
    }

    .ImageCard__tag--light-theme {
        display: block;
    }

    .ImageCard__tag--dark-theme {
        display: none;
    }

    .ImageCard__title-text {
        background: linear-gradient(
                to right,
                rgba(var(--white-rgb), 1),
                rgba(var(--white-rgb), 1)
            ),
            linear-gradient(
                to right,
                rgba(var(--black-rgb), 1),
                rgba(var(--black-rgb), 1)
            );
        background-size:
            100% 0.1em,
            0 0.1em;
        background-position:
            100% 100%,
            0 100%;
        background-repeat: no-repeat;
    }
}

.ImageCard {
    text-decoration: none;

    &:hover {
        .ImageCard__image,
        .ImageCard__chip {
            transform: translate(4px, 4px);
        }

        .ImageCard__image-container {
            &:before {
                transform: translate(-4px, -4px);
            }
        }

        .ImageCard__title-text {
            background-size:
                0 0.1em,
                100% 0.1em;
        }

        .ImageCard__list-info-separator {
            transform: rotate(225deg);
        }
    }
}

.ImageCard__image,
.ImageCard__image-container:before,
.ImageCard__chip {
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.ImageCard__image-container {
    position: relative;

    &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(
            --page-theme-base-colour,
            var(--image-card-base-colour, var(--lime-green))
        );
    }
}

.ImageCard__chip {
    position: absolute;
    left: 1.25rem;
    bottom: 1rem;
    z-index: 1;
    width: 30%;
    min-width: 8rem;
    max-width: 15rem;

    @include medium-up {
        min-width: 10rem;
        width: 30%;
    }
}

.ImageCard__title {
    @include body-sm;
    margin-top: 1.5rem;
    font-weight: var(--font-weight-regular);

    @media (min-width: 720px) {
        @include body-sm;
    }

    @include medium-up {
        margin-top: 1rem;
    }

    @include large-up {
        margin-top: 1.5rem;
        @include body-lg;
    }
}

.ImageCard__title-text {
    background: linear-gradient(
            to right,
            rgba(var(--black-rgb), 1),
            rgba(var(--black-rgb), 1)
        ),
        linear-gradient(
            to right,
            rgba(var(--white-rgb), 1),
            rgba(var(--white-rgb), 1)
        );
    background-size:
        100% 0.1em,
        0 0.1em;
    background-position:
        100% 100%,
        0 100%;
    background-repeat: no-repeat;
    transition: background-size 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.ImageCard__logo {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    height: 4.375rem;
    max-width: 7.5rem;

    @include medium-up {
        height: 5rem;
        max-width: 10rem;
    }
}

.ImageCard__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    margin-top: 1.5rem;
}

.ImageCard__tag--light-theme {
    display: none;
}

.ImageCard__list-info {
    @include small-only {
        @include body-caption;
    }

    display: flex;
    flex-wrap: wrap;
    font-weight: var(--font-weight-regular);
    margin-top: 1.5rem;

    li {
        display: flex;
        align-items: center;

        &:last-child {
            .ImageCard__list-info-separator {
                display: none;
            }
        }
    }
}

.ImageCard__list-info-separator {
    display: block;
    background-color: var(
        --page-theme-base-colour,
        var(--image-card-base-colour, var(--lime-green))
    );
    width: 0.45rem;
    height: 0.45rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    transform: rotate(45deg);
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    @include medium-up {
        width: 0.75rem;
        height: 0.75rem;
    }

    @include large-up {
        margin-left: 1rem;
        margin-right: 1rem;
    }
}

.ImageCard--small-text {
    .ImageCard__list-info {
        @include body-caption;
    }

    .ImageCard__list-info-separator {
        width: 0.5rem;
        height: 0.5rem;

        @include medium-up {
            width: 0.5rem;
            height: 0.5rem;
        }
    }

    .ImageCard__title {
        @include body-sm;

        @include large-up {
            @include body-sm;
        }
    }
}
</style>
